/**
 * Translation Provider Component
 * 
 * Wraps the app and provides translation functionality
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import TranslationContext from './TranslationContext';
import translationService from '../services/translationService';
import dictionary from './dictionary';
import { ENV_CONFIG } from '../config/environment-unified';

const TranslationProvider = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState('fr');
  const [isTranslating, setIsTranslating] = useState(false);
  const [uiTranslations, setUiTranslations] = useState(dictionary);

  // Supported languages
  const supportedLanguages = useMemo(() => [
    { code: 'fr', name: 'Français', flag: '🇫🇷' },
    { code: 'en', name: 'English', flag: '🇬🇧' }
  ], []);

  // Initialize translation service and load saved language
  useEffect(() => {
    console.log('🌐 [TranslationProvider] Initializing with URL:', ENV_CONFIG.TRANSLATION_API_URL);
    
    translationService.init({
      apiUrl: ENV_CONFIG.TRANSLATION_API_URL,
      defaultLanguage: ENV_CONFIG.DEFAULT_LANGUAGE || 'fr',
    });

    // Load saved language preference
    const savedLanguage = translationService.getCurrentLanguage();
    console.log('🌐 [TranslationProvider] Saved language:', savedLanguage);
    
    if (savedLanguage && savedLanguage !== 'fr') {
      setCurrentLanguage(savedLanguage);
      loadLanguageTranslations(savedLanguage);
    } else {
      // Initialize with French dictionary
      setUiTranslations(dictionary);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Load or generate translations for a language
   */
  const loadLanguageTranslations = useCallback(async (langCode) => {
    if (langCode === 'fr') {
      setUiTranslations(dictionary);
      setIsTranslating(false);
      return;
    }

    setIsTranslating(true);

    // PRIORITY 1: Try to load from static file first (server-side cache)
    console.log(`📂 [Translation] Loading ${langCode} from static file: /static/translations/${langCode}.json`);
    
    try {
      const response = await fetch(`/static/translations/${langCode}.json`, {
        cache: 'no-cache',
        headers: { 'Accept': 'application/json' }
      });
      
      console.log(`📡 [Translation] Static file response: ${response.status} ${response.statusText}`);
      
      if (response.ok) {
        const data = await response.json();
        
        if (data.translations && Object.keys(data.translations).length > 0) {
          console.log(`✅ [Translation] Loaded ${Object.keys(data.translations).length} translations from STATIC FILE (${langCode})`);
          console.log(`🎯 [Translation] NO API CALLS - Using pre-generated translations!`);
          
          setUiTranslations(data.translations);
          setIsTranslating(false);
          
          // Also save to browser cache for offline support
          translationService.saveUICache(langCode, data.translations);
          
          return; // ✅ SUCCESS - Exit early!
        }
      }
    } catch (fetchError) {
      console.warn(`⚠️ [Translation] Static file fetch failed:`, fetchError.message);
    }
    
    // PRIORITY 2: If static file not found, try browser localStorage cache
    console.log(`📦 [Translation] Static file not available, checking browser cache...`);
    
    try {
      const cached = translationService.loadUICache(langCode);
      const dictionaryKeys = Object.keys(dictionary);
      const cachedKeys = Object.keys(cached);
      
      console.log(`📊 [Translation] Cache status: ${cachedKeys.length}/${dictionaryKeys.length} keys`);
      
      // Check if cache is complete (at least 95% of dictionary)
      const isCacheComplete = cachedKeys.length >= dictionaryKeys.length * 0.95;
      
      if (isCacheComplete) {
        console.log(`✅ [Translation] Using complete browser cache (${cachedKeys.length} keys)`);
        setUiTranslations(cached);
        setIsTranslating(false);
        return; // ✅ SUCCESS - Exit early!
      }
      
      // PRIORITY 3: Last resort - translate via API (ONLY if no static file AND incomplete cache)
      console.warn(`⚠️ [Translation] No static file and incomplete cache!`);
      console.warn(`⚠️ [Translation] Falling back to API translation (this is slow!)`);
      console.warn(`⚠️ [Translation] Run 'npm run translate' to generate static files!`);
      
      const items = dictionaryKeys.map(key => ({
        key,
        text: dictionary[key],
      }));

      const batchSize = 10;
      const translations = { ...cached };
      
      for (let i = 0; i < items.length; i += batchSize) {
        const batch = items.slice(i, i + batchSize);
        const batchResults = await translationService.batchTranslateUI(batch, langCode);
        Object.assign(translations, batchResults);
        
        setUiTranslations({ ...translations });
        console.log(`📊 [Translation] API Progress: ${Math.min(i + batchSize, items.length)}/${items.length}`);
      }

      setUiTranslations(translations);
      console.log(`✅ [Translation] Completed API translation - ${Object.keys(translations).length} keys`);
    } catch (error) {
      console.error('❌ [Translation] Fatal error:', error);
      setUiTranslations(dictionary);
    } finally {
      setIsTranslating(false);
    }
  }, []);

  /**
   * Change current language
   */
  const changeLanguage = useCallback(async (langCode) => {
    if (!translationService.isLanguageSupported(langCode)) {
      console.warn(`⚠️ [Translation] Unsupported language: ${langCode}`);
      return;
    }

    console.log(`🌐 [Translation] Changing language from ${currentLanguage} to ${langCode}`);
    
    // Update language state first
    setCurrentLanguage(langCode);
    translationService.setCurrentLanguage(langCode);
    
    // Clear session storage (server response cache) to force re-translation of API responses
    const cacheKey = translationService.getResponseCacheKey(currentLanguage);
    sessionStorage.removeItem(cacheKey);
    console.log(`🗑️ [Translation] Cleared response cache for ${currentLanguage}`);
    
    // Load translations for new language
    await loadLanguageTranslations(langCode);
    
    // Force a re-render by updating a timestamp or counter if needed
    console.log(`✅ [Translation] Language changed to ${langCode}`);
  }, [loadLanguageTranslations, currentLanguage]);

  /**
   * Translate a UI string by key
   */
  const t = useCallback((key, fallback = null) => {
    // If translating and no cache, show key
    if (isTranslating && Object.keys(uiTranslations).length === 0) {
      return fallback || dictionary[key] || key;
    }

    // For French, use dictionary directly
    if (currentLanguage === 'fr') {
      return dictionary[key] || fallback || key;
    }

    // For other languages, use cached translations
    return uiTranslations[key] || fallback || dictionary[key] || key;
  }, [currentLanguage, uiTranslations, isTranslating]);

  /**
   * Translate dynamic text (server responses, etc.)
   */
  const translateText = useCallback(async (text) => {
    if (!text || currentLanguage === 'fr') {
      return text;
    }

    try {
      return await translationService.translateText(text, currentLanguage);
    } catch (error) {
      console.error('❌ [Translation] Error translating text:', error);
      return text; // Fallback to original
    }
  }, [currentLanguage]);

  const value = useMemo(() => ({
    currentLanguage,
    changeLanguage,
    t,
    translateText,
    isTranslating,
    supportedLanguages,
  }), [currentLanguage, changeLanguage, t, translateText, isTranslating, supportedLanguages]);

  return (
    <TranslationContext.Provider value={value}>
      {children}
    </TranslationContext.Provider>
  );
};

export default TranslationProvider;

